import { CurrencyEnum } from '@systemeio/ui'
import { AFFILIATE_SETTINGS_API } from 'modules/profile/affiliate-settings/api/affiliate-settings-api'
import AddBankDetailsModal from 'modules/profile/affiliate-settings/components/add-bank-details-modal'
import EditBankDetailsModal from 'modules/profile/affiliate-settings/components/edit-bank-details-modal'
import { useWiseRecipientApi } from 'modules/profile/affiliate-settings/hooks/use-wise-recipient-api'
import {
  AffiliateSettingsInterface,
  WiseRecipientId,
  WiseRecipientInterface,
} from 'modules/profile/affiliate-settings/types/affiliate-settings-interface'
import React from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import { KeyedMutator } from 'swr'

interface BankTransferTableProps {
  affiliateSettingsData: AffiliateSettingsInterface
  mutateAffiliateSettings: KeyedMutator<AffiliateSettingsInterface>
}

interface BankTransferTableDataInterface {
  items: BankTransferTableDataInterfaceItem[]
  hasMore: boolean
}

interface BankTransferTableDataInterfaceItem {
  id: WiseRecipientId
  currency: CurrencyEnum
  wiseRecipient: WiseRecipientInterface | null
}

function BankTransferTable({
  affiliateSettingsData,
  mutateAffiliateSettings,
}: BankTransferTableProps) {
  const { t } = useLocoTranslation()
  const updateData = useUpdateData({ api: AFFILIATE_SETTINGS_API })

  const { deleteWiseRecipientDetails } = useWiseRecipientApi()

  const bankTransferTableData: BankTransferTableDataInterface = {
    items: affiliateSettingsData.allowedCurrencies.map((currency, index) => {
      return {
        id:
          affiliateSettingsData.wiseRecipients.find(
            wiseRecipient => wiseRecipient.currency === currency,
          )?.id || index,
        key: currency,
        currency: currency,
        wiseRecipient:
          affiliateSettingsData.wiseRecipients.find(
            wiseRecipient => wiseRecipient.currency === currency,
          ) || null,
      }
    }),
    hasMore: false,
  }

  const bankTransferTableColumns: ColumnDefinitionInterface<BankTransferTableDataInterfaceItem>[] =
    [
      {
        key: 'currency',
        header: t('global.currency'),
        widthPercent: 50,
        onRender: data => <div>{data.currency.toUpperCase()}</div>,
      },
      {
        key: 'wiseRecipient',
        header: t('dashboard.user_settings.affiliate_commissions.wire_table.bank_details_title'),
        widthPercent: 50,
        onRender: data => {
          if (data.wiseRecipient) {
            return (
              <div className={'flex flex-col gap-1.5'}>
                <EditBankDetailsModal
                  wiseRecipient={data.wiseRecipient}
                  mutateAffiliateSettings={mutateAffiliateSettings}
                />
                {data.wiseRecipient.errorMessage && (
                  <p className={'text-danger whitespace-normal'}>
                    {data.wiseRecipient.errorMessage}
                  </p>
                )}
              </div>
            )
          }
          return (
            <AddBankDetailsModal
              currency={data.currency}
              mutateAffiliateSettings={mutateAffiliateSettings}
            />
          )
        },
      },
    ]

  const removeWiseRecipientDetails = (id: WiseRecipientId) => async () => {
    await deleteWiseRecipientDetails(id)
    await updateData(false, affiliateSettingsData)
  }

  return (
    <>
      {affiliateSettingsData.allowedCurrencies.length > 0 ? (
        <Table
          actions={{
            custom: [
              {
                name: 'delete',
                shouldRender: data => !!data.wiseRecipient,
                caption: t('dashboard.actions.title.remove'),
                onActionRender: (opened, onClose, instance, key) => (
                  <ConfirmationModal
                    opened={opened}
                    onClose={onClose}
                    onConfirm={removeWiseRecipientDetails(instance.id)}
                    confirmationText={t(
                      'dashboard.user_settings.affiliate_commissions.wire_table.remove_confirmation',
                    )}
                    title={t('confirmation_modal.default_title')}
                    key={key}
                  />
                ),
              },
            ],
          }}
          data={bankTransferTableData}
          columns={bankTransferTableColumns}
        />
      ) : (
        <div className="text-yellow">
          {t('dashboard.user_settings.affiliate_commissions.wire_table.sale_warning_title')}
        </div>
      )}
    </>
  )
}

export default BankTransferTable
